/**
 * As abas de "minha conta" do modal de configuracoes.
 *
 * Separado do `SettingsModal` de proposito: aquele arquivo trata da
 * administracao da comunidade, e o que esta aqui e do individuo — vale em
 * qualquer lugar, nao depende de permissao nenhuma e nao some quando alguem
 * perde um cargo. A casca (lateral de abas, titulo, fechar) continua sendo a
 * do modal; so o conteudo vem daqui.
 */

import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { MIN_PASSWORD_LENGTH, passwordError } from "@shared/protocol";
import type { ActiveSession } from "@shared/types";
import {
  changePassword,
  fetchSessions,
  revokeOtherSessions,
  revokeSession,
} from "@/lib/account";
import { useApp } from "@/state/store";
import { C, FONT_MONO, FONT_SANS, hexA, initialOf } from "@/theme";

const MONO_LABEL: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
};

const FIELD: CSSProperties = {
  padding: "10px 12px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 9,
  background: C.field,
  color: C.text,
  fontSize: 14,
  outline: 0,
};

const SECTION_TITLE: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: C.textStrong,
};

const HINT: CSSProperties = {
  marginTop: 4,
  fontSize: 12.5,
  lineHeight: 1.55,
  color: C.textMuted,
};

const CARD: CSSProperties = {
  padding: 16,
  border: `1px solid ${C.lineSoft}`,
  borderRadius: 12,
  background: C.field,
};

function primaryButton(disabled: boolean): CSSProperties {
  return {
    padding: "9px 16px",
    border: 0,
    borderRadius: 9,
    cursor: disabled ? "default" : "pointer",
    background: "var(--ac, #1F4FD8)",
    color: C.ink,
    fontFamily: FONT_SANS,
    fontSize: 13,
    fontWeight: 700,
    opacity: disabled ? 0.55 : 1,
    transition: "opacity 150ms ease",
  };
}

function dangerButton(disabled = false): CSSProperties {
  return {
    padding: "9px 16px",
    border: `1px solid ${hexA(C.red, 0.45)}`,
    borderRadius: 9,
    cursor: disabled ? "default" : "pointer",
    background: hexA(C.red, 0.12),
    color: C.red,
    fontFamily: FONT_SANS,
    fontSize: 13,
    fontWeight: 600,
    opacity: disabled ? 0.55 : 1,
  };
}

/* -------------------------------------------------------------------- */
/* Avisos                                                                */
/* -------------------------------------------------------------------- */

function Banner({ kind, children }: { kind: "erro" | "ok"; children: string }) {
  const color = kind === "erro" ? C.red : C.green;
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "9px 11px",
        borderRadius: 9,
        border: `1px solid ${hexA(color, 0.35)}`,
        background: hexA(color, 0.12),
        fontSize: 12.5,
        lineHeight: 1.45,
        color,
        animation: "fadein 150ms ease",
      }}
    >
      <span style={{ width: 7, height: 7, flex: "0 0 auto", borderRadius: 999, background: color }} />
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Aba: minha conta                                                      */
/* -------------------------------------------------------------------- */

export function AccountTab() {
  const { state, actions } = useApp();
  const me = state.me;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  const problem =
    next.length === 0
      ? null
      : (passwordError(next) ?? (next === confirm ? null : "As duas senhas novas não batem."));
  const ready = current.length > 0 && next.length > 0 && confirm.length > 0 && problem === null;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || !ready) return;

    setBusy(true);
    setError(null);
    setDone(false);
    const result = await changePassword(current, next);
    setBusy(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }
    // O servidor invalidou o token anterior ao derrubar os outros aparelhos.
    // Adotar o novo aqui e o que mantem esta aba funcionando.
    actions.applySession(result.data.sessionToken);
    setCurrent("");
    setNext("");
    setConfirm("");
    setDone(true);
  }

  if (!me) return null;

  return (
    <div style={{ display: "grid", gap: 24, maxWidth: 520 }}>
      {/* Quem sou eu */}
      <div style={{ ...CARD, display: "flex", alignItems: "center", gap: 14 }}>
        {me.avatarUrl ? (
          <img
            src={me.avatarUrl}
            alt=""
            style={{ width: 52, height: 52, borderRadius: 999, objectFit: "cover", flex: "0 0 auto" }}
          />
        ) : (
          <div
            style={{
              width: 52,
              height: 52,
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              borderRadius: 999,
              background: me.color ?? "var(--ac, #1F4FD8)",
              color: C.ink,
              fontSize: 21,
              fontWeight: 700,
            }}
          >
            {initialOf(me.name)}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{me.name}</div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: C.textMuted, marginTop: 2 }}>
            @{me.handle}
          </div>
        </div>
        <button
          type="button"
          onClick={() => actions.openProfileEditor()}
          className="hov-bg-24"
          style={{
            padding: "8px 14px",
            border: `1px solid ${C.lineStrong}`,
            borderRadius: 9,
            cursor: "pointer",
            background: "transparent",
            color: C.textBody,
            fontFamily: FONT_SANS,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Editar perfil
        </button>
      </div>

      {/* Trocar senha */}
      <form onSubmit={event => void submit(event)} style={{ display: "grid", gap: 12 }}>
        <div>
          <div style={SECTION_TITLE}>Trocar a senha</div>
          <div style={HINT}>
            Pedimos a senha atual porque é ela que prova que é você — sem isso, um aparelho
            esquecido aberto viraria posse definitiva da conta. Ao trocar, você sai de todos os
            outros aparelhos automaticamente.
          </div>
        </div>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={MONO_LABEL}>SENHA ATUAL</span>
          <input
            type="password"
            value={current}
            onChange={event => {
              setCurrent(event.target.value);
              setError(null);
              setDone(false);
            }}
            autoComplete="current-password"
            style={FIELD}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={MONO_LABEL}>SENHA NOVA</span>
          <input
            type="password"
            value={next}
            onChange={event => {
              setNext(event.target.value);
              setError(null);
              setDone(false);
            }}
            placeholder={`pelo menos ${MIN_PASSWORD_LENGTH} caracteres`}
            autoComplete="new-password"
            style={FIELD}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={MONO_LABEL}>REPITA A SENHA NOVA</span>
          <input
            type="password"
            value={confirm}
            onChange={event => {
              setConfirm(event.target.value);
              setError(null);
              setDone(false);
            }}
            autoComplete="new-password"
            style={FIELD}
          />
        </label>

        {problem ? (
          <span style={{ fontFamily: FONT_MONO, fontSize: 10, lineHeight: 1.5, color: C.red }}>
            {problem}
          </span>
        ) : null}
        {error ? <Banner kind="erro">{error}</Banner> : null}
        {done ? <Banner kind="ok">Senha trocada. Os outros aparelhos foram desconectados.</Banner> : null}

        <div>
          <button type="submit" disabled={busy || !ready} style={primaryButton(busy || !ready)}>
            {busy ? "Trocando…" : "Trocar senha"}
          </button>
        </div>
      </form>

      {/* Sair */}
      <div style={{ borderTop: `1px solid ${C.lineSoft}`, paddingTop: 20 }}>
        <div style={SECTION_TITLE}>Sair da conta</div>
        <div style={HINT}>
          Encerra a sessão neste aparelho. Os outros continuam conectados — para derrubá-los, use
          a aba Sessões ativas.
        </div>
        <div style={{ marginTop: 12 }}>
          {confirmingLogout ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" onClick={() => actions.logout()} style={dangerButton()}>
                Confirmar e sair
              </button>
              <button
                type="button"
                onClick={() => setConfirmingLogout(false)}
                style={{
                  padding: "9px 14px",
                  border: 0,
                  borderRadius: 9,
                  cursor: "pointer",
                  background: "transparent",
                  color: C.textMuted,
                  fontSize: 13,
                }}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmingLogout(true)} style={dangerButton()}>
              Sair da conta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Aba: sessões ativas                                                   */
/* -------------------------------------------------------------------- */

/**
 * Rotulo legivel a partir do texto cru do navegador. Heuristica simples e
 * assumidamente incompleta — o valor original fica no `title` da linha, para
 * quem precisar conferir o que o palpite escondeu.
 */
function deviceLabel(userAgent: string | null): string {
  if (!userAgent) return "Aparelho desconhecido";
  const ua = userAgent;

  const app = /LFRCORD|Electron/i.test(ua)
    ? "App desktop"
    : /Edg\//.test(ua)
      ? "Edge"
      : /OPR\/|Opera/.test(ua)
        ? "Opera"
        : /Firefox\//.test(ua)
          ? "Firefox"
          : /Chrome\//.test(ua)
            ? "Chrome"
            : /Safari\//.test(ua)
              ? "Safari"
              : "Navegador";

  const os = /Windows NT/.test(ua)
    ? "Windows"
    : /Android/.test(ua)
      ? "Android"
      : /iPhone|iPad|iPod/.test(ua)
        ? "iOS"
        : /Mac OS X/.test(ua)
          ? "macOS"
          : /Linux/.test(ua)
            ? "Linux"
            : null;

  return os ? `${app} no ${os}` : app;
}

function relativeTime(stamp: number): string {
  const diff = Date.now() - stamp;
  if (diff < 2 * 60_000) return "agora";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days} ${days === 1 ? "dia" : "dias"}`;
  return new Date(stamp).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function SessionsTab() {
  const { actions } = useApp();
  const [sessions, setSessions] = useState<ActiveSession[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await fetchSessions();
    if (!result.ok) {
      setError(result.message);
      setSessions([]);
      return;
    }
    setError(null);
    setAvailable(result.data.available);
    setSessions(result.data.sessions);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function endOne(sessionId: string): Promise<void> {
    setWorking(sessionId);
    setError(null);
    const result = await revokeSession(sessionId);
    setWorking(null);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setAvailable(result.data.available);
    setSessions(result.data.sessions);
  }

  async function endOthers(): Promise<void> {
    setWorking("todas");
    setError(null);
    const result = await revokeOtherSessions();
    setWorking(null);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    // Token novo: o anterior acabou de ser invalidado junto com os outros.
    actions.applySession(result.data.sessionToken);
    await load();
  }

  const others = (sessions ?? []).filter(session => !session.current);

  return (
    <div style={{ display: "grid", gap: 18, maxWidth: 620 }}>
      <div>
        <div style={SECTION_TITLE}>Onde você está conectado</div>
        <div style={HINT}>
          Cada linha é uma entrada na conta. Encerrar uma derruba aquele aparelho na hora — o
          token dele para de valer, não só some da máquina.
        </div>
      </div>

      {error ? <Banner kind="erro">{error}</Banner> : null}

      {!available ? (
        <div
          style={{
            ...CARD,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: C.textMuted,
            borderColor: hexA(C.amber, 0.28),
            background: hexA(C.amber, 0.08),
          }}
        >
          A lista de sessões ainda não está disponível neste servidor: falta aplicar a migração
          <span style={{ fontFamily: FONT_MONO }}> sql/2026-08-24-contas-e-sessoes.sql </span>
          no banco. Trocar a senha e sair de todos os lugares continuam funcionando normalmente.
        </div>
      ) : sessions === null ? (
        <div style={{ ...HINT, marginTop: 0 }}>Carregando…</div>
      ) : sessions.length === 0 ? (
        <div style={{ ...HINT, marginTop: 0 }}>Nenhuma sessão registrada.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {sessions.map(session => (
            <div
              key={session.id}
              title={session.userAgent ?? undefined}
              style={{
                ...CARD,
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 14px",
                borderColor: session.current ? hexA(C.green, 0.35) : C.lineSoft,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {deviceLabel(session.userAgent)}
                  </span>
                  {session.current ? (
                    <span
                      style={{
                        padding: "2px 7px",
                        borderRadius: 999,
                        background: hexA(C.green, 0.16),
                        color: C.green,
                        fontFamily: FONT_MONO,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: ".1em",
                      }}
                    >
                      ESTE APARELHO
                    </span>
                  ) : null}
                </div>
                <div
                  style={{
                    marginTop: 3,
                    fontFamily: FONT_MONO,
                    fontSize: 10.5,
                    letterSpacing: ".04em",
                    color: C.textGhost,
                  }}
                >
                  {session.ip ? `${session.ip} · ` : ""}
                  ativa {relativeTime(session.lastSeenAt)} · entrou{" "}
                  {new Date(session.createdAt).toLocaleDateString("pt-BR")}
                </div>
              </div>

              {session.current ? null : (
                <button
                  type="button"
                  disabled={working !== null}
                  onClick={() => void endOne(session.id)}
                  style={{ ...dangerButton(working !== null), padding: "7px 12px", fontSize: 12 }}
                >
                  {working === session.id ? "Encerrando…" : "Encerrar"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {others.length > 0 ? (
        <div style={{ borderTop: `1px solid ${C.lineSoft}`, paddingTop: 16 }}>
          <button
            type="button"
            disabled={working !== null}
            onClick={() => void endOthers()}
            style={dangerButton(working !== null)}
          >
            {working === "todas"
              ? "Encerrando…"
              : `Encerrar as outras ${others.length} sessões`}
          </button>
          <div style={HINT}>
            Você continua conectado aqui. Use isto se desconfiar que alguém entrou na sua conta —
            e troque a senha em seguida.
          </div>
        </div>
      ) : null}
    </div>
  );
}
